import * as React from 'react';
import styled, {css} from 'styled-components';
import {lighten, transparentize} from 'polished';
import CourseBuilderContext
  from 'components/CourseBuilder/CourseBuilderContext';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import {
  ChapterContentObject,
  ChapterContentType,
  ChapterObject,
  GrowplaceProgram,
} from 'api/course';
import {getLessonByID} from 'api/lesson/requests';
import {LessonObject} from 'api/lesson/types';
import LoaderContainer from 'containers/LoaderContainer';
import DragControl from 'components/DragControl';
import ControlButton from 'components/ControlButton';
import Button, {ButtonVariant} from 'components/Button';
import InstancePicker, {InstanceType} from 'components/InstancePicker';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import useModal from 'hooks/use-modal';
import {transition} from 'helpers/css';
import {
  IconEdit, IconFood,
  IconLesson, IconLessons,
  IconReplace,
  IconTrash,
  IconWebinar,
  IconWorkout,
} from 'icons';
import {getWebinarByID} from 'api/webinar/requests';
import {WebinarObject} from 'api/webinar/types';
import {toast} from 'react-toastify';
import {v4 as uuid} from 'uuid';
import {useDrawer} from '../../../../../../../Drawer';
import LessonBuilder from '../../../../../../../LessonBuilder';
import {getProgram} from '../../../../../../../../api/program/requests';
import {
  FoodItem,
  ProgramObject,
  TrainingItem,
} from '../../../../../../../../api/program/types';

export type ContentElementProps = ChapterContentObject & {
  chapterId: ChapterObject['id'];
  index: number;
};

const FixedHeight = styled.div`
  height: 51px;
  border-top: 1px solid ${({theme}) => `
    ${theme.variables.borderColor};
  `};
`;

const Container = styled.div<DraggableStateSnapshot>`
  padding: 5px 25px 5px 30px;

  ${transition()};
  background: ${({theme}) => theme.variables.accentBackgroundColor};
  border-color: ${({theme}) => theme.variables.borderColor};
  
  ${({isDragging, isDropAnimating}) => isDragging && !isDropAnimating ? css`
    border-radius: 5px;
    box-shadow: 3px 24px 60px rgba(25, 25, 27, .5);
    background: ${({theme}) => `
      ${lighten(.025, theme.variables.accentBackgroundColor)};
    `};
    border: 1px solid ${({theme}) => `
      ${theme.variables.borderColor};
    `};
  ` : css`
    border-top: 1px solid ${({theme}) => `
      ${theme.variables.borderColor};
    `};
  `};
`;

const Content = styled.div`
  border-radius: 5px;
  padding: 1px 20px 1px 8px;
  column-gap: 12px;
  align-items: center;
  grid-template-columns: min-content min-content 1fr max-content;
  display: grid;
`;

const IconContainer = styled.div`
  display: grid;
  color: ${({theme}) => `
    ${transparentize(.7, theme.colors.white)};
  `};
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  user-select: none;
`;

const Note = styled.div`
  justify-content: space-between;
  align-items: center;
  display: flex;
  margin-right: -45px;
`;

const NoteControls = styled.div`
  gap: 6px;
  align-content: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const Controls = styled.div`
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

/**
 * This component renders the chapter content element
 * @param {ContentElementProps} props
 * @constructor
 */
export default function ContentElement(
    props: ContentElementProps,
): React.ReactElement<ContentElementProps> {
  const lessonPicker = useModal();
  const webinarPicker = useModal();
  const trainingPicker = useModal();
  const foodPicker = useModal();

  const editLessonDrawer = useDrawer({
    align: 'right',
  });

  const {
    course,
    setCourse,
  } = React.useContext(CourseBuilderContext);

  const [
    instance,
    setInstance,
  ] = React.useState<
    | LessonObject
    | WebinarObject
    | ProgramObject<TrainingItem>
    | ProgramObject<FoodItem>
    | undefined
  >(undefined);

  React.useEffect(() => {
    switch (props.type) {
      case ChapterContentType.Lesson: {
        setTimeout(() => {
          getLessonByID(props.instanceId).then((response) => {
            validateResponse(response);
            !isError(response) && (
              setInstance(response)
            );
          });
        }, 500);
        break;
      }
      case ChapterContentType.Webinar: {
        setTimeout(() => {
          getWebinarByID(props.instanceId).then((response) => {
            validateResponse(response);
            !isError(response) && (
              setInstance(response)
            );
          });
        }, 500);
        break;
      }
      case ChapterContentType.Training: {
        setTimeout(() => {
          getProgram<TrainingItem>(props.instanceId).then((response) => {
            validateResponse(response);
            !isError(response) && (
              setInstance(response)
            );
          });
        }, 500);
        break;
      }
      case ChapterContentType.Food: {
        setTimeout(() => {
          getProgram<FoodItem>(props.instanceId).then((response) => {
            validateResponse(response);
            !isError(response) && (
              setInstance(response)
            );
          });
        }, 500);
        break;
      }
    }
  }, [props.instanceId]);

  /**
   * This function renders the interaction control
   * and passes the necessary properties to it.
   * @param {DraggableProvided} provided
   * @param {DraggableStateSnapshot} snapshot
   * @return {JSX.Element}
   */
  function renderDragControl(
      provided: DraggableProvided,
      snapshot: DraggableStateSnapshot,
  ): JSX.Element {
    return (
      <DragControl
        provided={provided}
        snapshot={snapshot}
      />
    );
  }

  /**
   * This function will render the content type icon.
   * @return {JSX.Element}
   */
  function renderIcon(): JSX.Element {
    /**
     * This function returns an icon.
     * @return {JSX.Element}
     */
    function getIcon(): JSX.Element | undefined {
      switch (props.type) {
        case ChapterContentType.Lesson: return (
          <IconLessons />
        )
        case ChapterContentType.Webinar: return (
          <IconWebinar />
        )
        case ChapterContentType.Training: return (
          <IconWorkout />
        )
        case ChapterContentType.Food: return (
          <IconFood />
        )
      }
    }
    return (
      <IconContainer>
        {getIcon()}
      </IconContainer>
    );
  }

  /**
   * This function will render the title.
   * @param {string} title
   * @return {JSX.Element}
   */
  function renderTitle(title: string): JSX.Element {
    return (
      <Title>
        {title}
      </Title>
    );
  }

  function renderControls(): JSX.Element {
    /**
     * This function will render the delete control.
     * @return {JSX.Element}
     */
    function renderDelete(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        /**
         * The delete event handler.
         */
        function handleDelete(): void {
          const program = course?.programs;
          const content = program?.content as GrowplaceProgram;

          content.chapters.map((chapter) => {
            if (chapter.id === props.chapterId) {
              chapter.content = chapter.content?.filter((content) => (
                content.id !== props.id
              ));
            }
          });

          (program && setCourse) && setCourse({
            ...course,
            programs: {
              ...program,
              content,
            },
          });
        }

        toast.info((
          <Note>
            <span>Вы действительно хотите удалить элемент из глвы?</span>
            <NoteControls>
              <Button type="button" variant={ButtonVariant.Danger} onClick={handleDelete}>
                Удалить
              </Button>
              <Button type="button" variant={ButtonVariant.Outline}>
                Отменить
              </Button>
            </NoteControls>
          </Note>
        ), {
          autoClose: false,
          closeButton: false,
        });
      }

      return (
        <ControlButton onClick={handleClick}>
          <IconTrash />
        </ControlButton>
      );
    }

    /**
     * Render edit.
     * @return {JSX.Element}
     */
    function renderEdit(): JSX.Element {
      return (
        <ControlButton onClick={() => {
          switch (props.type) {
            case ChapterContentType.Lesson: editLessonDrawer.open();
          }
        }}>
          <IconEdit />
        </ControlButton>
      );
    }

    /**
     * Render replace.
     * @return {JSX.Element}
     */
    function renderReplace(): JSX.Element {
      /**
       * The click event handler.
       * @return {void}
       */
      function handleClick(): void {
        switch (props.type) {
          case ChapterContentType.Lesson: {
            lessonPicker.setModalVisible(true);
            break;
          }
          case ChapterContentType.Webinar: {
            webinarPicker.setModalVisible(true);
            break;
          }
          case ChapterContentType.Training: {
            trainingPicker.setModalVisible(true);
            break;
          }
          case ChapterContentType.Food: {
            foodPicker.setModalVisible(true);
            break;
          }
        }
      }

      /**
       * This blur lesson picker event handler.
       */
      function handleBlur(): void {
        lessonPicker.setModalVisible(false);
        webinarPicker.setModalVisible(false);
        trainingPicker.setModalVisible(false);
        foodPicker.setModalVisible(false);
      }

      /**
       * This select event handler.
       * @param {LessonObject} selected
       */
      function handleSelect(selected: LessonObject['id'][]): void {
        const program = course?.programs;
        const content = program?.content as GrowplaceProgram;
        const chapter = content?.chapters.find((chapter) => (
          chapter.id === props.chapterId
        ));

        if (!chapter) return;

        chapter.content = chapter.content || [];
        chapter.content = [
          ...chapter.content?.map((element) => (
            element.id === props.id ? {
              id: props.id,
              type: props.type,
              instanceId: selected[0],
            } : element
          )),
        ];

        (program && setCourse) && setCourse({
          ...course,
          programs: {
            ...program,
            content,
          },
        });

        handleBlur();
      }

      return (
        <React.Fragment>
          <ControlButton onClick={handleClick}>
            <IconReplace />
          </ControlButton>
          {lessonPicker.isModalVisible && (
            <InstancePicker<LessonObject>
              renderHeader="Выберите урок"
              type={InstanceType.Lesson}
              onBlur={handleBlur}
              onSelect={handleSelect}
            />
          )}
          {webinarPicker.isModalVisible && (
            <InstancePicker<WebinarObject>
              renderHeader="Выберите вебинар"
              type={InstanceType.Webinar}
              onBlur={handleBlur}
              onSelect={handleSelect}
              multiply
            />
          )}
          {trainingPicker.isModalVisible && (
            <InstancePicker
              renderHeader="Выберите тренировку"
              type={InstanceType.Training}
              onBlur={handleBlur}
              onSelect={handleSelect}
              multiply
            />
          )}
          {foodPicker.isModalVisible && (
            <InstancePicker
              renderHeader="Выберите питание"
              type={InstanceType.Food}
              onBlur={handleBlur}
              onSelect={handleSelect}
              multiply
            />
          )}
          {editLessonDrawer.render(
            <LessonBuilder lessonId={props.instanceId} />
          )}
        </React.Fragment>
      );
    }

    return (
      <Controls>
        {renderEdit()}
        {renderReplace()}
        {renderDelete()}
      </Controls>
    );
  }

  return instance ? (
    <Draggable draggableId={props.id} index={props.index}>
      {(provided, snapshot) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <Container {...snapshot}>
            <Content>
              {renderDragControl(provided, snapshot)}
              {renderIcon()}
              {renderTitle(instance.title)}
              {renderControls()}
            </Content>
          </Container>
        </div>
      )}
    </Draggable>
  ) : (
    <FixedHeight>
      <LoaderContainer
        width="100%"
        height="51px"
        preserveAspectRatio="none"
      >
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
        />
      </LoaderContainer>
    </FixedHeight>
  );
}
